// frontend/src/pages/LearnOverview.jsx
import React from "react";
import { motion } from "framer-motion";

export default function LearnOverview() {
  return (
    <motion.div
      className="min-h-screen bg-blue-50 text-slate-800 dark:bg-slate-900 dark:text-slate-100 pb-12 md:pb-16"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      {/* Hero */}
      <header className="bg-white/90 dark:bg-slate-900/90 backdrop-blur shadow-md border-b-4 border-blue-600 dark:border-blue-500">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-10 md:py-16 text-center">
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-blue-800 dark:text-blue-300 mb-3 md:mb-4">
            Learn Crypto Trading
          </h1>
          <p className="text-blue-700/90 dark:text-blue-200/90 text-base sm:text-lg md:text-xl max-w-2xl mx-auto">
            Master the different types of trades and strategies to become a confident crypto trader.
          </p>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-4 sm:px-6 mt-10 md:mt-16 space-y-12 md:space-y-20">
        {/* Trading Types Overview */}
        <section>
          <h2 className="text-2xl sm:text-3xl font-semibold text-blue-800 dark:text-blue-300 mb-4 md:mb-6">
            Types of Trades
          </h2>
          <ul className="space-y-6 md:space-y-8 text-slate-700 dark:text-slate-300">
            <li className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 md:p-6 shadow-sm">
              <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-blue-700 dark:text-blue-300 mb-1.5 md:mb-2">
                📉 Spot Trading
              </h3>
              <p className="text-sm sm:text-base">
                Spot trading involves buying or selling cryptocurrencies instantly at the current market price.
                On Coinzetto, you can place spot orders via Quick Trade or through the Exchange interface.
              </p>
            </li>

            <li className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 md:p-6 shadow-sm">
              <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-blue-700 dark:text-blue-300 mb-1.5 md:mb-2">
                🤝 P2P Trading
              </h3>
              <p className="text-sm sm:text-base">
                Peer-to-peer (P2P) trading allows users to buy and sell crypto directly with each other using fiat payment methods.
                Coinzetto plans to introduce a secure P2P platform where buyers and sellers are matched safely using escrow mechanisms.
              </p>
            </li>

            <li className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 md:p-6 shadow-sm">
              <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-blue-700 dark:text-blue-300 mb-1.5 md:mb-2">
                📈 Futures Trading
              </h3>
              <p className="text-sm sm:text-base">
                Futures trading involves speculating on the price of a cryptocurrency at a future date.
                Traders can go “long” or “short” with leverage. This is a high-risk, high-reward option
                and is suitable for experienced users only. Coinzetto will offer Futures soon with full compliance and risk controls.
              </p>
            </li>

            <li className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 md:p-6 shadow-sm">
              <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-blue-700 dark:text-blue-300 mb-1.5 md:mb-2">
                🔁 Arbitrage Trading
              </h3>
              <p className="text-sm sm:text-base">
                Arbitrage involves taking advantage of price differences across exchanges or pairs.
                For example, you might buy BTC on one platform at ₹2,600,000 and sell it on another at ₹2,620,000.
                Arbitrage traders profit from price inefficiencies, but must act quickly and factor in fees.
              </p>
            </li>

            <li className="rounded-2xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 p-4 md:p-6 shadow-sm">
              <h3 className="text-lg sm:text-xl md:text-2xl font-semibold text-blue-700 dark:text-blue-300 mb-1.5 md:mb-2">
                📊 Algorithmic &amp; Bot Trading
              </h3>
              <p className="text-sm sm:text-base">
                Advanced traders use automated strategies and trading bots to execute high-frequency trades
                or rebalance portfolios. Coinzetto provides API access for developers to integrate such tools.
              </p>
            </li>
          </ul>
        </section>

        {/* Key Concepts */}
        <section>
          <h2 className="text-2xl sm:text-3xl font-semibold text-blue-800 dark:text-blue-300 mb-4 md:mb-6">
            Essential Trading Concepts
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 md:gap-8 text-slate-700 dark:text-slate-300">
            {[
              {
                title: "Market Order",
                text: "Executes instantly at the best available price. Ideal for speed, not price precision.",
              },
              {
                title: "Limit Order",
                text: "Lets you set your desired buy or sell price. Executes only when the market hits that price.",
              },
              {
                title: "Stop-Loss",
                text: "A safety mechanism to automatically sell if the price drops below a certain point to limit your losses.",
              },
              {
                title: "Leverage",
                text: "Borrowed capital used to increase exposure. Can multiply gains—but also losses. Used in futures trading.",
              },
            ].map((card, i) => (
              <motion.div
                key={card.title}
                whileHover={{ y: -3 }}
                transition={{ type: "spring", stiffness: 250, damping: 20 }}
                className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-4 sm:p-5 md:p-6 rounded-2xl shadow-sm"
              >
                <h3 className="text-base sm:text-lg md:text-xl font-semibold text-blue-700 dark:text-blue-300 mb-1.5">
                  {card.title}
                </h3>
                <p className="text-sm sm:text-base">{card.text}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Learn Tools */}
        <section>
          <h2 className="text-2xl sm:text-3xl font-semibold text-blue-800 dark:text-blue-300 mb-4 md:mb-6">
            Learning Tools &amp; Resources
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6 md:gap-8 text-slate-700 dark:text-slate-300">
            {[
              {
                title: "Video Tutorials",
                text: "Short, beginner-friendly videos explaining how to trade, deposit, withdraw, and use Coinzetto.",
              },
              {
                title: "Glossary",
                text: "Understand crypto jargon—clear definitions for over 100 commonly used blockchain and trading terms.",
              },
              {
                title: "Blog & Market Insights",
                text: "Explore articles on current trends, platform updates, and trading strategies to stay ahead of the curve.",
              },
              {
                title: "Ask Support",
                text: "Our team is always available to help if you’re confused—just raise a support ticket anytime.",
              },
            ].map((card) => (
              <motion.div
                key={card.title}
                whileHover={{ y: -3 }}
                transition={{ type: "spring", stiffness: 250, damping: 20 }}
                className="bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-4 sm:p-5 md:p-6 rounded-2xl shadow-sm"
              >
                <h3 className="text-base sm:text-lg md:text-xl font-semibold text-blue-700 dark:text-blue-300 mb-1.5">
                  {card.title}
                </h3>
                <p className="text-sm sm:text-base">{card.text}</p>
              </motion.div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="text-center pt-2 md:pt-8">
          <h2 className="text-2xl sm:text-3xl font-semibold text-blue-800 dark:text-blue-300 mb-2 md:mb-4">
            Ready to Practice?
          </h2>
          <p className="text-sm sm:text-base text-slate-700 dark:text-slate-300 mb-4 md:mb-6 max-w-xl mx-auto">
            Open your Coinzetto account and try trading risk-free with a test deposit or real INR.
          </p>
          <a
            href="/signup"
            className="inline-flex items-center justify-center rounded-xl px-5 py-2.5 sm:px-8 sm:py-3 text-sm sm:text-base font-medium bg-blue-600 text-white hover:bg-blue-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-400 dark:focus-visible:ring-blue-500 shadow"
          >
            Start Trading
          </a>
        </section>
      </main>
    </motion.div>
  );
}
